import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { TableAction, TableColumn } from 'src/app/shared/components/data-table/data-table.component';
import { IconType } from 'src/app/modules/authentication/components/models/authentication-actions.enum';
import { ApiService } from 'src/app/shared/services/api.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { UserService } from 'src/app/shared/services/user.service';
import { environment } from 'src/environments/environment';

interface ManagedUser {
  id: number;
  email: string;
  fullName?: string;
  mobile?: string;
  isActive: boolean;
  isEmailVerified: boolean;
  role?: number | null;
  roleName?: string;
  mustChangePassword: boolean;
  lastLoginDate?: string;
  roleDisplay?: string;
  status?: string;
  emailStatus?: string;
}

interface RoleOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserManagementComponent implements OnInit {
  users: ManagedUser[] = [];
  tableColumns: TableColumn[] = [];
  showUserModal = false;
  showPasswordModal = false;
  modalMode: 'create' | 'edit' = 'create';
  selectedUser: ManagedUser | null = null;
  showPassword = false;
  showConfirmPassword = false;

  roleOptions: RoleOption[] = [
    { label: 'TCM', value: 1 },
    { label: 'Vice Chairman', value: 2 },
    { label: 'Chairman', value: 3 },
    { label: 'Secretary', value: 4 },
    { label: 'Observer', value: 5 },
    { label: 'Evaluator', value: 6 },
    { label: 'Procurement Officer', value: 7 }
  ];

  userForm = {
    fullName: '',
    email: '',
    mobile: '',
    role: 1,
    isActive: true,
    isEmailVerified: true,
    mustChangePassword: true,
    password: '',
    confirmPassword: ''
  };

  passwordForm = {
    password: '',
    confirmPassword: '',
    mustChangePassword: true
  };

  private apiService = inject(ApiService);
  private loaderService = inject(LoaderService);
  private toasterService = inject(ToasterService);
  private userService = inject(UserService);
  private dialogService = inject(DialogService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.userService.initializeFromLocalStorage();
    if (!this.canCurrentUserManageUsers()) {
      this.toasterService.showError('Only Secretary can manage users.', 'Access Denied');
      void this.router.navigate([AppConstants.ROUTES.HOME]);
      return;
    }

    this.initializeTableColumns();
    void this.loadUsers();
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#532E60" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#532E60" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        action: 'edit',
        title: 'Edit',
        iconSize: 'small'
      },
      {
        icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 2L19 4M19 4L22 7L18.5 10.5L15.5 7.5M19 4L15.5 7.5M11.39 11.61C11.9063 12.3068 12.1191 13.1808 11.9845 14.0374C11.8499 14.8939 11.3783 15.6657 10.6701 16.1657C9.96187 16.6657 9.07355 16.8535 8.17929 16.6907C7.28503 16.528 6.45777 16.0283 5.85355 15.3957C5.24933 14.7632 4.91383 13.9989 4.91041 13.206C4.90699 12.4131 5.23591 11.6443 5.83574 11.0065C6.43556 10.3686 7.26095 9.90516 8.1537 9.7023C9.04646 9.49944 9.95284 9.56936 10.706 9.899" stroke="#532E60" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        action: 'password',
        title: 'Reset Password',
        iconSize: 'small'
      },
      {
        icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#B42318" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="#B42318" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 8L22 13M22 8L17 13" stroke="#B42318" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        action: 'deactivate',
        title: 'Deactivate',
        iconSize: 'small',
        condition: (row: ManagedUser) => row.isActive
      },
      {
        icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#047857" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="#047857" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 11L19 13L23 9" stroke="#047857" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        action: 'activate',
        title: 'Activate',
        iconSize: 'small',
        condition: (row: ManagedUser) => !row.isActive
      }
    ];

    this.tableColumns = [
      { field: 'fullName', header: 'Full Name', type: 'text', sortable: true },
      { field: 'email', header: 'Email', type: 'text', sortable: true },
      { field: 'roleDisplay', header: 'Role', type: 'text', sortable: true },
      { field: 'mobile', header: 'Mobile', type: 'text', sortable: true },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        sortable: true,
        badgeColor: (row: ManagedUser) => row.isActive ? 'green' : 'red'
      },
      {
        field: 'emailStatus',
        header: 'Email Status',
        type: 'badge',
        sortable: true,
        badgeColor: (row: ManagedUser) => row.isEmailVerified ? 'green' : 'orange'
      },
      { field: 'lastLoginDate', header: 'Last Login', type: 'date', format: 'dd MMM yyyy', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: actionIcons, sortable: false, filterable: false }
    ];
  }

  async loadUsers(): Promise<void> {
    this.loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.USER}/${AppConstants.ACTIONS.Admin_GetUsers}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      if (response?.isSuccess || response?.IsSuccess) {
        const data = response?.data || response?.Data || [];
        this.users = Array.isArray(data) ? data.map((item: any) => this.mapUser(item)) : [];
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to load users', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while loading users', 'Error');
    } finally {
      this.loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  openCreateModal(): void {
    this.modalMode = 'create';
    this.selectedUser = null;
    this.userForm = {
      fullName: '',
      email: '',
      mobile: '',
      role: 1,
      isActive: true,
      isEmailVerified: true,
      mustChangePassword: true,
      password: '',
      confirmPassword: ''
    };
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.showUserModal = true;
    this.cdr.markForCheck();
  }

  openEditModal(user: ManagedUser): void {
    this.modalMode = 'edit';
    this.selectedUser = user;
    this.userForm = {
      fullName: user.fullName || '',
      email: user.email || '',
      mobile: user.mobile || '',
      role: this.normalizeRoleValue(user.role ?? user.roleName ?? user.roleDisplay) || 1,
      isActive: user.isActive,
      isEmailVerified: user.isEmailVerified,
      mustChangePassword: user.mustChangePassword,
      password: '',
      confirmPassword: ''
    };
    this.showUserModal = true;
    this.cdr.markForCheck();
  }

  closeUserModal(): void {
    this.showUserModal = false;
    this.selectedUser = null;
    this.cdr.markForCheck();
  }

  async saveUser(): Promise<void> {
    if (!this.validateUserForm()) {
      return;
    }

    const payload: any = {
      fullName: this.userForm.fullName.trim(),
      email: this.userForm.email.trim(),
      mobile: this.userForm.mobile.trim(),
      role: Number(this.userForm.role),
      isActive: this.userForm.isActive,
      isEmailVerified: this.userForm.isEmailVerified,
      mustChangePassword: this.userForm.mustChangePassword
    };

    if (this.modalMode === 'create') {
      payload.password = this.userForm.password;
      payload.confirmPassword = this.userForm.confirmPassword;
    }

    this.loaderService.showLoader();
    try {
      const action = this.modalMode === 'create'
        ? AppConstants.ACTIONS.Admin_CreateUser
        : AppConstants.ACTIONS.Admin_UpdateUser;
      const query = this.modalMode === 'edit' && this.selectedUser
        ? `?userId=${encodeURIComponent(String(this.selectedUser.id))}`
        : '';
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.USER}/${action}${query}`;
      const response: any = this.modalMode === 'create'
        ? await lastValueFrom(this.apiService.postByUrl(url, payload))
        : await lastValueFrom(this.apiService.putByUrl(url, payload));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess(this.modalMode === 'create' ? 'User created successfully' : 'User updated successfully', 'Success');
        this.closeUserModal();
        await this.loadUsers();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to save user', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while saving user', 'Error');
    } finally {
      this.loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  openPasswordModal(user: ManagedUser): void {
    this.selectedUser = user;
    this.passwordForm = {
      password: '',
      confirmPassword: '',
      mustChangePassword: true
    };
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.showPasswordModal = true;
    this.cdr.markForCheck();
  }

  closePasswordModal(): void {
    this.showPasswordModal = false;
    this.selectedUser = null;
    this.cdr.markForCheck();
  }

  async resetPassword(): Promise<void> {
    if (!this.selectedUser || !this.validatePasswordFields(this.passwordForm.password, this.passwordForm.confirmPassword)) {
      return;
    }

    this.loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.USER}/${AppConstants.ACTIONS.Admin_ResetUserPassword}?userId=${encodeURIComponent(String(this.selectedUser.id))}`;
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, {
        password: this.passwordForm.password,
        confirmPassword: this.passwordForm.confirmPassword,
        mustChangePassword: this.passwordForm.mustChangePassword
      }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Password reset successfully', 'Success');
        this.closePasswordModal();
        await this.loadUsers();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to reset password', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while resetting password', 'Error');
    } finally {
      this.loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  onTableActionClick(event: { action: string, row: ManagedUser }): void {
    if (event.action === 'edit') {
      this.openEditModal(event.row);
      return;
    }

    if (event.action === 'password') {
      this.openPasswordModal(event.row);
      return;
    }

    if (event.action === 'activate' || event.action === 'deactivate') {
      this.confirmToggleUserStatus(event.row);
    }
  }

  confirmToggleUserStatus(user: ManagedUser): void {
    const nextState = user.isActive ? 'deactivate' : 'activate';
    this.dialogService.showDialog(
      `${nextState === 'activate' ? 'Activate' : 'Deactivate'} User`,
      `Are you sure you want to ${nextState} ${user.fullName || user.email}?`,
      IconType.Warning,
      true,
      () => void this.toggleUserStatus(user),
      true
    );
  }

  async toggleUserStatus(user: ManagedUser): Promise<void> {
    this.dialogService.hideDialog();
    this.loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.USER}/${AppConstants.ACTIONS.Admin_SetUserActive}?userId=${encodeURIComponent(String(user.id))}`;
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, { isActive: !user.isActive }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess(!user.isActive ? 'User activated successfully' : 'User deactivated successfully', 'Success');
        await this.loadUsers();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to update user status', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'An error occurred while updating user status', 'Error');
    } finally {
      this.loaderService.hideLoader();
      this.cdr.markForCheck();
    }
  }

  getRoleLabel(user: ManagedUser): string {
    return user.roleName || this.roleOptions.find(role => role.value === Number(user.role))?.label || '-';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  private mapUser(item: any): ManagedUser {
    const role = this.normalizeRoleValue(item.role ?? item.Role ?? item.roleName ?? item.RoleName);
    const isActive = item.isActive ?? item.IsActive ?? false;
    const isEmailVerified = item.isEmailVerified ?? item.IsEmailVerified ?? false;

    return {
      id: item.id ?? item.Id ?? item.ID,
      email: item.email ?? item.Email ?? '',
      fullName: item.fullName ?? item.FullName ?? '-',
      mobile: item.mobile ?? item.Mobile ?? '-',
      isActive,
      isEmailVerified,
      role,
      roleName: item.roleName ?? item.RoleName,
      mustChangePassword: item.mustChangePassword ?? item.MustChangePassword ?? false,
      lastLoginDate: item.lastLoginDate ?? item.LastLoginDate,
      roleDisplay: this.roleOptions.find(option => option.value === Number(role))?.label ?? item.roleName ?? item.RoleName ?? '-',
      status: isActive ? 'Active' : 'Inactive',
      emailStatus: isEmailVerified ? 'Verified' : 'Unverified'
    };
  }

  private validateUserForm(): boolean {
    if (!this.userForm.email.trim()) {
      this.toasterService.showError('Email is required', 'Validation Error');
      return false;
    }

    if (!this.userForm.email.includes('@')) {
      this.toasterService.showError('A valid email is required', 'Validation Error');
      return false;
    }

    if (!this.userForm.role) {
      this.toasterService.showError('Role is required', 'Validation Error');
      return false;
    }

    if (this.modalMode === 'create' && !this.validatePasswordFields(this.userForm.password, this.userForm.confirmPassword)) {
      return false;
    }

    return true;
  }

  private validatePasswordFields(password: string, confirmPassword: string): boolean {
    if (!password?.trim() || !confirmPassword?.trim()) {
      this.toasterService.showError('Password and confirm password are required', 'Validation Error');
      return false;
    }

    if (password !== confirmPassword) {
      this.toasterService.showError('Password and confirm password do not match', 'Validation Error');
      return false;
    }

    const isStrong = password.length >= 8
      && /[A-Z]/.test(password)
      && /[a-z]/.test(password)
      && /\d/.test(password)
      && /[^A-Za-z0-9]/.test(password);

    if (!isStrong) {
      this.toasterService.showError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character', 'Validation Error');
      return false;
    }

    return true;
  }

  private canCurrentUserManageUsers(): boolean {
    const role = (this.userService.user?.Role || '').toString().trim().toUpperCase().replace(/\s+/g, '');
    return role === 'SECRETARY' || role === 'TENDERCOMMITTEESECRETARY';
  }

  private normalizeRoleValue(role: any): number | null {
    if (role === null || role === undefined || role === '') {
      return null;
    }

    const numericRole = Number(role);
    if (!Number.isNaN(numericRole) && this.roleOptions.some(option => option.value === numericRole)) {
      return numericRole;
    }

    const normalizedRole = role.toString().trim().toUpperCase().replace(/\s+/g, '');
    const roleMap: Record<string, number> = {
      TCM: 1,
      VICECHAIRMAN: 2,
      CHAIRMAN: 3,
      SECRETARY: 4,
      TENDERCOMMITTEESECRETARY: 4,
      OBSERVER: 5,
      EVALUATOR: 6,
      PROCURMENTOFFICER: 7,
      PROCUREMENTOFFICER: 7
    };

    return roleMap[normalizedRole] ?? null;
  }
}
